import { buildBlockRecord, Client } from '@datocms/cma-client-browser';
import ApiClient from '@src/apiClient/ApiClient';
import { AIFlowLog, AIFlowLogContext } from './AIFlow';
import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('aiFlow:logSaver');

/**
 * Persists AI flow logs to DatoCMS, used by AIFlowRepository.
 */
export async function saveAiFlowLogs(
  datoClient: Client | ApiClient,
  logs: AIFlowLog[],
  logContext: AIFlowLogContext = {},
): Promise<string | undefined> {
  if (logs.length === 0) return;

  const flowTitle = logs[0].flow;
  const category = logs[0].category;

  const stepLogBlocks = [];
  try {
    const stepLogBlockTypeId = (
      await datoClient.itemTypes.find('ai_flow_step_log')
    ).id as string;
    const stepLogMessageBlockTypeId = (
      await datoClient.itemTypes.find('ai_flow_log_message')
    ).id as string;

    let currentStepLabel = '';
    let messageLogs = [];
    for (const log of logs) {
      if (log.stepLabel !== currentStepLabel && currentStepLabel) {
        stepLogBlocks.push(
          buildBlockRecord({
            item_type: { type: 'item_type', id: stepLogBlockTypeId },
            step_label: log.stepLabel,
            message_log: messageLogs,
          }),
        );

        messageLogs = [];
      }
      currentStepLabel = log.stepLabel;

      messageLogs.push(
        buildBlockRecord({
          item_type: { type: 'item_type', id: stepLogMessageBlockTypeId },
          timestamp: log.time,
          message: log.message,
        }),
      );
    }
    stepLogBlocks.push(
      buildBlockRecord({
        item_type: { type: 'item_type', id: stepLogBlockTypeId },
        step_label: logs[logs.length - 1].stepLabel,
        message_log: messageLogs,
      }),
    );
  } catch (err) {
    logger.error('Error building step log blocks', { error: err });
  }

  const record = {
    flow_title: flowTitle,
    category,
    logs: stepLogBlocks,
    ...(logContext.aiFlowId && { ai_flow: logContext.aiFlowId }),
    ...(logContext.personId && { person: logContext.personId }),
    ...(logContext.storyId && { story: logContext.storyId }),
    ...(logContext.showcaseId && { showcase: logContext.showcaseId }),
    ...(logContext.metadata && { metadata: JSON.stringify(logContext.metadata) }),
    ...(logContext.latency_ms != null && { latency_ms: logContext.latency_ms }),
    ...(logContext.first_content_latency_ms != null && {
      first_content_latency_ms: logContext.first_content_latency_ms,
    }),
  };

  logger.debug('AI flow log record to save', {
    flowTitle,
    latency_ms: record.latency_ms,
    first_content_latency_ms: record.first_content_latency_ms,
    hasLatency: 'latency_ms' in record,
    hasFirstContent: 'first_content_latency_ms' in record,
  });

  try {
    const itemType = await datoClient.itemTypes.find('ai_flow_log');
    const savedLog = await datoClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      ...record,
    });
    logger.info('Saved AI flow log', { id: savedLog.id, flowTitle });
    return savedLog.id;
  } catch (err: any) {
    logger.error('Failed to save AI flow log', {
      error: err?.message || err,
      statusCode: err?.statusCode,
      flowTitle,
    });
  }
}
